import React, { useState } from "react";
import { AlertTriangle, ChevronsUp, CheckCheck, UserCheck, PauseCircle, RotateCcw, Tag } from "lucide-react";

const card = "rounded-xl border border-[#00ff90]/30 bg-[#0d0d0d] shadow-[0_0_12px_#00ff9022]";

const PRI_COLOR = { P1:"text-red-400", P2:"text-orange-400", P3:"text-yellow-400", P4:"text-blue-400" };
const STATUS_STYLE = {
  "Open":        "text-[#00ff90] bg-[#00ff90]/10 border-[#00ff90]/20",
  "Pending":     "text-yellow-400 bg-yellow-500/10 border-yellow-500/20",
  "In Progress": "text-blue-400 bg-blue-500/10 border-blue-500/20",
  "Escalated":   "text-orange-400 bg-orange-500/10 border-orange-500/20",
  "On Hold":     "text-purple-400 bg-purple-500/10 border-purple-500/20",
  "Closed":      "text-white/25 bg-white/5 border-white/10",
};

function dueColor(due) {
  if (!due) return "text-white/20";
  const ms = new Date(due).getTime() - Date.now();
  if (ms < 0) return "text-red-400";
  if (ms < 7200000) return "text-yellow-400";
  return "text-white/30";
}

export default function TicketGrid({
  rows, onOpen, onAssign, onMerge, onSplit,
  onBulkEscalate, onBulkClose, onBulkHold, onBulkReopen,
  onBulkReassign, onBulkCategory,
  assignees = []
}) {
  const [selection,      setSelection]      = useState(new Set());
  const [showReassign,   setShowReassign]   = useState(false);
  const [showCategory,   setShowCategory]   = useState(false);
  const [reassignTarget, setReassignTarget] = useState("");
  const [categoryTarget, setCategoryTarget] = useState("");

  const toggle    = (id) => setSelection(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = (checked) => setSelection(checked ? new Set(rows.map(r => r.id)) : new Set());
  const anySelected = selection.size > 0;
  const ASSIGNEES = assignees.length > 0 ? assignees : ["HR Payroll","HR Ops","IT Help","IT L2"];

  const doBulkReassign = () => { if (reassignTarget) { onBulkReassign?.([...selection], reassignTarget); setShowReassign(false); setReassignTarget(""); setSelection(new Set()); } };
  const doBulkCategory = () => { if (categoryTarget) { onBulkCategory?.([...selection], categoryTarget); setShowCategory(false); setCategoryTarget(""); setSelection(new Set()); } };

  return (
    <div className={card}>
      <div className="px-5 py-4 border-b border-white/5 flex items-center justify-between flex-wrap gap-3">
        <span className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium">All Tickets</span>
        <div className="flex items-center gap-2 flex-wrap">
          {anySelected && (
            <>
              <span className="text-xs text-[#00ff90]/50">{selection.size} selected</span>

              <button onClick={() => onBulkEscalate?.([...selection])}
                className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg border border-orange-500/25 bg-orange-500/8 text-orange-400/70 hover:bg-orange-500/15 hover:text-orange-400 transition-colors">
                <ChevronsUp size={12} /> Escalate
              </button>
              <button onClick={() => onBulkHold?.([...selection])}
                className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg border border-purple-500/25 bg-purple-500/8 text-purple-400/70 hover:bg-purple-500/15 hover:text-purple-400 transition-colors">
                <PauseCircle size={12} /> Hold
              </button>
              <button onClick={() => onBulkReopen?.([...selection])}
                className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg border border-[#00ff90]/25 bg-[#00ff90]/8 text-[#00ff90]/70 hover:bg-[#00ff90]/15 hover:text-[#00ff90] transition-colors">
                <RotateCcw size={12} /> Reopen
              </button>
              <button onClick={() => onBulkClose?.([...selection])}
                className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg border border-white/10 bg-white/5 text-white/40 hover:text-white transition-colors">
                <CheckCheck size={12} /> Close
              </button>

              {/* Bulk reassign */}
              <div className="relative">
                <button onClick={() => { setShowReassign(v => !v); setShowCategory(false); }}
                  className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg border border-blue-500/25 bg-blue-500/8 text-blue-400/70 hover:bg-blue-500/15 hover:text-blue-400 transition-colors">
                  <UserCheck size={12} /> Reassign
                </button>
                {showReassign && (
                  <div className="absolute right-0 top-8 z-10 rounded-xl border border-[#00ff90]/20 bg-[#0d0d0d] p-3 shadow-lg w-48">
                    <select className="w-full bg-black border border-[#00ff90]/20 rounded-lg px-2 py-1.5 text-sm text-white outline-none mb-2"
                      value={reassignTarget} onChange={e => setReassignTarget(e.target.value)}>
                      <option value="">Select agent…</option>
                      {ASSIGNEES.map(a => <option key={a}>{a}</option>)}
                    </select>
                    <button onClick={doBulkReassign} disabled={!reassignTarget}
                      className="w-full text-xs py-1.5 rounded-lg bg-[#00ff90]/15 text-[#00ff90] border border-[#00ff90]/30 disabled:opacity-40 transition-colors">
                      Apply
                    </button>
                  </div>
                )}
              </div>

              {/* Bulk category */}
              <div className="relative">
                <button onClick={() => { setShowCategory(v => !v); setShowReassign(false); }}
                  className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg border border-white/10 bg-white/5 text-white/40 hover:text-white transition-colors">
                  <Tag size={12} /> Category
                </button>
                {showCategory && (
                  <div className="absolute right-0 top-8 z-10 rounded-xl border border-[#00ff90]/20 bg-[#0d0d0d] p-3 shadow-lg w-44">
                    <select className="w-full bg-black border border-[#00ff90]/20 rounded-lg px-2 py-1.5 text-sm text-white outline-none mb-2"
                      value={categoryTarget} onChange={e => setCategoryTarget(e.target.value)}>
                      <option value="">Select…</option>
                      {["IT","Payroll","Leave","Facilities","HR","Finance"].map(c => <option key={c}>{c}</option>)}
                    </select>
                    <button onClick={doBulkCategory} disabled={!categoryTarget}
                      className="w-full text-xs py-1.5 rounded-lg bg-[#00ff90]/15 text-[#00ff90] border border-[#00ff90]/30 disabled:opacity-40 transition-colors">
                      Apply
                    </button>
                  </div>
                )}
              </div>
            </>
          )}

          {selection.size >= 2 && (
            <button onClick={() => onMerge?.([...selection])}
              className="text-xs px-3 py-1.5 rounded-lg border border-[#00ff90]/20 bg-[#00ff90]/5 text-[#00ff90]/60 hover:bg-[#00ff90]/15 hover:text-[#00ff90] transition-colors">
              Merge
            </button>
          )}
          {selection.size === 1 && (
            <button onClick={() => onSplit?.([...selection][0])}
              className="text-xs px-3 py-1.5 rounded-lg border border-white/10 bg-white/5 text-white/40 hover:text-white transition-colors">
              Split
            </button>
          )}
          <span className="text-xs text-white/30">{rows?.length || 0} records</span>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-white/5">
              <th className="px-4 py-3 w-8">
                <input type="checkbox" className="w-4 h-4 rounded accent-[#00ff90]"
                  onChange={e => toggleAll(e.target.checked)} />
              </th>
              {["ID","Subject","Category","Priority","Status","Requester","Due","Assignee",""].map(h => (
                <th key={h} className="px-3 py-3 text-left text-xs text-[#00ff90]/40 uppercase tracking-wider whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {!rows?.length ? (
              <tr><td colSpan={10} className="px-4 py-8 text-center text-white/20 italic text-sm">No tickets match filters.</td></tr>
            ) : rows.map(t => {
              const statusStyle = STATUS_STYLE[t.status] || STATUS_STYLE.Open;
              return (
                <tr key={t.id} className={`border-b border-white/5 hover:bg-white/3 transition-colors ${t.breached ? "bg-red-500/3" : ""}`}>
                  <td className="px-4 py-3">
                    <input type="checkbox" className="w-4 h-4 rounded accent-[#00ff90]"
                      checked={selection.has(t.id)} onChange={() => toggle(t.id)} />
                  </td>
                  <td className="px-3 py-3">
                    <div className="flex items-center gap-1.5">
                      {t.breached && <AlertTriangle size={11} className="text-red-400 shrink-0" />}
                      <span className="text-xs text-white/40 font-mono">{t.id}</span>
                    </div>
                  </td>
                  <td className="px-3 py-3">
                    <button onClick={() => onOpen?.(t)} className="text-left hover:text-[#00ff90] transition-colors">
                      <p className="text-sm text-white/80 font-medium">{t.subject}</p>
                    </button>
                  </td>
                  <td className="px-3 py-3 text-white/50 text-xs">{t.category}</td>
                  <td className="px-3 py-3">
                    <span className={`text-xs font-bold ${PRI_COLOR[t.priority] || "text-white/40"}`}>{t.priority}</span>
                  </td>
                  <td className="px-3 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full border ${statusStyle}`}>{t.status}</span>
                  </td>
                  <td className="px-3 py-3 text-white/50 text-xs">{t.requester}</td>
                  <td className={`px-3 py-3 text-xs ${dueColor(t.due)}`}>
                    {t.due ? new Date(t.due).toLocaleDateString("en-GB", { day:"2-digit", month:"short" }) : "—"}
                  </td>
                  <td className="px-3 py-3">
                    <select value={t.assignee || ""} onChange={e => onAssign?.(t.id, e.target.value)}
                      className="bg-black border border-white/10 rounded-lg px-2 py-1 text-xs text-white outline-none focus:border-[#00ff90]/40 transition-colors">
                      <option value="">Unassigned</option>
                      {ASSIGNEES.map(a => <option key={a} value={a}>{a}</option>)}
                    </select>
                  </td>
                  <td className="px-3 py-3">
                    <button onClick={() => onOpen?.(t)}
                      className="text-xs px-3 py-1.5 rounded-lg border border-[#00ff90]/20 bg-[#00ff90]/5 text-[#00ff90]/60 hover:bg-[#00ff90]/15 hover:text-[#00ff90] transition-colors">
                      Open
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}